<template>
    <div>
        <Head>
            <title>Кабінет</title>
        </Head>
        <Header pageName="Кабінет" :cart-count="cartCount"/>
        <div class="mx-auto max-w-2xl mt-4 px-4 py-4 sm:px-6 sm:py-8 lg:max-w-7xl lg:px-8">
            <flash-message v-if="flash.success" type="success" :message="flash.success" class="mb-4"/>
            <flash-message v-if="flash.error" type="error" :message="flash.error" class="mb-4"/>
            <div>
                <h2 class="page-title">Мої замовлення</h2>
            </div>
            <div
                class="flex flex-col md:flex-row mt-4 items-center md:justify-between"
                :class="isOwner ? 'justify-between' : 'justify-end'"
            >
                <div v-if="isOwner" class="flex flex-col md:flex-row justify-start items-center mb-4 md:mb-0">
                    <Link
                        :href="route('table.reports')"
                        class="flex items-center h-12 btn-indigo uppercase tracking-widest btn-booking text-uppercase rounded-lg justify-center"
                    >
                        Адмін панель
                    </Link>
                    <Link
                        :href="route('cabinet.watchman')"
                        class="flex items-center h-12 btn-indigo uppercase tracking-widest btn-booking text-uppercase rounded-lg justify-center sm:ml-4"
                    >
                        Кабінет сторожа
                    </Link>
                </div>
                <div class="p-4 bg-white">
                    <p><span class="inline-block w-4 h-4 bg-green-700 mr-2 rounded-sm"></span> Замовлення оплачено</p>
                    <p><span class="inline-block w-4 h-4 bg-gray-200 mr-2"></span> Замовлення не оплачено</p>
                </div>
            </div>
            <!-- Internal balance block -->
            <div class="bg-white rounded-md shadow mt-4 p-6">
                <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <p class="text-sm text-gray-500">Внутрішній баланс</p>
                        <p class="text-2xl font-bold text-indigo-700">{{ user.internal_balance ? Number(user.internal_balance).toFixed(2) : '0.00' }} грн</p>
                        <p v-if="bonusPercent > 0" class="text-sm text-green-600 mt-1">Ваш бонус за замовлення: {{ bonusPercent }}%</p>
                        <p v-else class="text-sm text-gray-400 mt-1">Бонус за замовлення: немає</p>
                    </div>
                    <form @submit.prevent="submitTopup" class="flex items-center gap-2">
                        <input
                            v-model="topupAmount"
                            type="number"
                            min="1"
                            max="10000"
                            step="1"
                            placeholder="Сума, грн"
                            class="border border-gray-300 rounded-lg px-3 py-2 w-36 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                            required
                        />
                        <button
                            type="submit"
                            class="h-10 btn-indigo uppercase tracking-widest btn-booking text-uppercase rounded-lg px-4 text-sm mb-0"
                            :disabled="topupLoading"
                        >
                            {{ topupLoading ? '...' : 'Поповнити' }}
                        </button>
                    </form>
                </div>
            </div>

            <div class="bg-white rounded-md shadow mt-4" v-for="order in orders.data" :key="order.id">

                <!-- Desktop Table View -->
                <div class="hidden md:block overflow-x-auto">
                    <table class="w-full whitespace-nowrap">
                        <tr class="text-left font-bold">
                        <th class="pb-4 pt-6 px-6">Номер</th>
                        <th class="pb-4 pt-6 px-6">Ім'я</th>
                        <th class="pb-4 pt-6 px-6">Телефон</th>
                        <th class="pb-4 pt-6 px-6">Озеро</th>
                        <th class="pb-4 pt-6 px-6">Назва місця</th>
                        <th class="pb-4 pt-6 px-6">Тип</th>
                        <th class="pb-4 pt-6 px-6">Дата</th>
                        <th class="pb-4 pt-6 px-6">Чек</th>
                        <th v-if="order.is_payed" class="pb-4 pt-6 px-6">Послуги</th>
                        </tr>
                        <tr v-for="preOrder in order.preOrders" :key="preOrder.id"
                            class="hover:bg-gray-100 focus-within:bg-gray-100 h-10"
                            :class="{'bg-green-300' : order.is_payed}"
                        >
                            <td class="border-t">
                                <p class="flex items-center px-6 py-4 focus:text-indigo-500">
                                    {{ order.id }}
                                </p>
                            </td>
                            <td class="border-t">
                                <p class="flex items-center px-6 py-4 focus:text-indigo-500">
                                    {{ order.name }}
                                </p>
                            </td>
                            <td class="border-t">
                                <p class="flex items-center px-6 py-4 focus:text-indigo-500">
                                    {{ order.phone }}
                                </p>
                            </td>
                            <td class="border-t">
                                <p class="flex items-center px-6 py-4 focus:text-indigo-500">
                                    {{ preOrder.lake.name }}
                                </p>
                            </td>
                            <td class="border-t">
                                <p class="flex items-center px-6 py-4 focus:text-indigo-500">
                                    {{ preOrder.point?.name ?? '' }}
                                </p>
                            </td>
                            <td class="border-t">
                                <p class="flex items-center px-6 py-4 focus:text-indigo-500">
                                    {{ preOrder.type?.name }}
                                </p>
                            </td>
                            <td class="border-t">
                                <div class="flex items-center px-6 py-4">
                                    <span class="focus:text-indigo-500">{{ formattedDate(preOrder.start_date) }}</span>
                                    <button v-if="order.is_payed && preOrder.point && isFutureDate(preOrder.start_date)" @click="startEditDate(preOrder)"
                                            class="ml-2 text-indigo-600 hover:text-indigo-900" title="Змінити дату">
                                        <icon name="edit" class="w-4 h-4 fill-current"/>
                                    </button>
                                </div>
                            </td>
                            <td class="border-t">
                                <a
                                    v-if="order.is_payed"
                                    class="flex items-center justify-center focus:text-indigo-500"
                                    :href="$route('invoice.download', encodeBase64(order.id))"
                                >
                                    <icon role="button" name="downloadPdf" class="block w-6 h-6 fill-gray-400"/>
                                </a>
                                <div v-else-if="!order.is_payed && couldBePayed(preOrder)" class="flex flex-col gap-2">
                                    <button
                                        class="items-center h-12 btn-indigo uppercase tracking-widest btn-booking text-uppercase rounded-lg justify-center"
                                        @click="checkout(order.id)"
                                    >
                                        Заплатити
                                    </button>
                                    <button
                                        v-if="user.internal_balance >= order.total_sum"
                                        class="items-center h-12 btn-indigo uppercase tracking-widest btn-booking text-uppercase rounded-lg justify-center"
                                        @click="payWithBalance(order.id)"
                                    >
                                        Сплатити з балансу
                                    </button>
                                </div>
                                <span
                                    v-else
                                    class="flex items-center justify-center focus:text-indigo-500"
                                >
                                    --
                                </span>
                            </td>
                            <!-- Cabinet attributes button — only for paid orders with a point -->
                            <td v-if="order.is_payed && preOrder.point" class="border-t px-4 py-2">
                                <div class="flex flex-col gap-1">
                                    <!-- Purchased paid attributes badge -->
                                    <div v-if="preOrder.cabinet_attributes && preOrder.cabinet_attributes.length > 0" class="flex flex-wrap gap-1 mb-1">
                                        <span
                                            v-for="attr in preOrder.cabinet_attributes"
                                            :key="attr.id"
                                            class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                                            :class="attr.is_payed ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'"
                                        >
                                            {{ attr.title }}
                                        </span>
                                    </div>
                                    <button
                                        @click="openAttributesModal(preOrder)"
                                        class="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 border border-indigo-200 hover:border-indigo-400 rounded px-2 py-1 transition-all whitespace-nowrap"
                                    >
                                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                                        </svg>
                                        Послуги
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </table>
                </div>

                <!-- Bonus awarded (desktop) -->
                <div v-if="order.is_payed && order.bonus_awarded > 0" class="hidden md:flex px-6 py-2 border-t items-center gap-2 text-sm text-green-700">
                    <span class="font-semibold">Нараховано бонусів:</span>
                    <span class="font-bold">+{{ Number(order.bonus_awarded).toFixed(2) }} грн</span>
                </div>

                <!-- Cancel order button (desktop) -->
                <div v-if="hasCancellablePreOrder(order)" class="hidden md:flex px-6 py-3 border-t justify-end">
                    <button @click="cancelOrder(order.id)"
                            class="text-sm text-red-600 hover:text-red-800 border border-red-300 hover:border-red-500 rounded px-4 py-1.5 transition-all">
                        Скасувати замовлення
                    </button>
                </div>

                <!-- Mobile Accordion View -->
                <div class="md:hidden w-full">
                    <div
                        class="p-4 border-b border-gray-200 flex justify-between items-center cursor-pointer"
                        :class="order.is_payed ? 'bg-green-700' : 'bg-white'"
                        @click="toggleOrder(order.id)"
                    >
                        <div>
                            <p class="font-bold" :class="order.is_payed ? 'text-white' : 'text-gray-900'">Замовлення #{{ order.id }}</p>
                            <p class="text-sm" :class="order.is_payed ? 'text-green-100' : 'text-gray-600'">{{ order.name }}</p>
                        </div>
                        <div class="flex items-center">
                            <span v-if="order.is_payed" class="text-xs font-bold text-white mr-2">Оплачено</span>
                            <span v-else class="text-xs font-bold text-gray-500 mr-2">Неоплачено</span>
                            <icon :name="expandedOrders.includes(order.id) ? 'cheveron-down' : 'cheveron-right'" class="w-5 h-5" :class="order.is_payed ? 'text-green-100' : 'text-gray-500'"/>
                        </div>
                    </div>

                    <div v-show="expandedOrders.includes(order.id)" class="p-4 bg-gray-50">
                        <div v-for="(preOrder, index) in order.preOrders" :key="preOrder.id" class="mb-4 pb-4 border-b border-gray-200 last:border-0 last:mb-0 last:pb-0">

                            <div class="grid grid-cols-2 gap-y-2 gap-x-4 text-sm mb-4">
                                <span class="font-bold text-gray-700">Озеро:</span> <span class="text-gray-900">{{ preOrder.lake.name }}</span>
                                <span class="font-bold text-gray-700">Місце:</span> <span class="text-gray-900">{{ preOrder.point?.name ?? '-' }}</span>
                                <span class="font-bold text-gray-700">Тип:</span> <span class="text-gray-900">{{ preOrder.type?.name }}</span>
                                <span class="font-bold text-gray-700">Дата:</span> <span class="text-gray-900">{{ formattedDate(preOrder.start_date) }}</span>
                            </div>

                            <!-- Cabinet attributes section (mobile) -->
                            <div v-if="order.is_payed && preOrder.point" class="mb-3">
                                <div v-if="preOrder.cabinet_attributes && preOrder.cabinet_attributes.length > 0" class="flex flex-wrap gap-1 mb-2">
                                    <span
                                        v-for="attr in preOrder.cabinet_attributes"
                                        :key="attr.id"
                                        class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                                        :class="attr.is_payed ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'"
                                    >
                                        {{ attr.title }}
                                    </span>
                                </div>
                            </div>

                            <div class="flex flex-wrap gap-2 mt-4">
                                <button v-if="order.is_payed && preOrder.point && isFutureDate(preOrder.start_date)" @click="startEditDate(preOrder)" class="flex-1 flex justify-center items-center text-indigo-700 border border-indigo-300 bg-indigo-50 hover:bg-indigo-100 px-3 py-2 rounded text-sm font-semibold transition">
                                    <icon name="edit" class="w-4 h-4 mr-2 fill-current"/> Змінити дату
                                </button>
                                <a v-if="order.is_payed" :href="$route('invoice.download', encodeBase64(order.id))" class="flex-1 flex justify-center items-center text-gray-700 border border-gray-300 bg-white hover:bg-gray-50 px-3 py-2 rounded text-sm font-semibold transition">
                                    <icon name="downloadPdf" class="w-4 h-4 mr-2 fill-current"/> Чек
                                </a>
                                <!-- Cabinet attributes button (mobile) -->
                                <button v-if="order.is_payed && preOrder.point" @click="openAttributesModal(preOrder)"
                                        class="flex-1 flex justify-center items-center gap-1.5 text-indigo-700 border border-indigo-300 bg-indigo-50 hover:bg-indigo-100 px-3 py-2 rounded text-sm font-semibold transition">
                                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                                    </svg>
                                    Послуги
                                </button>
                                <template v-else-if="!order.is_payed && couldBePayed(preOrder)">
                                    <button @click="checkout(order.id)" class="w-full flex justify-center items-center bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded text-sm font-semibold shadow-sm transition">
                                        Заплатити
                                    </button>
                                    <button
                                        v-if="user.internal_balance >= order.total_sum"
                                        @click="payWithBalance(order.id)"
                                        class="w-full flex justify-center items-center border border-indigo-600 text-indigo-600 hover:bg-indigo-50 px-4 py-2 rounded text-sm font-semibold transition"
                                    >
                                        Сплатити з балансу
                                    </button>
                                </template>
                            </div>
                        </div>
                        <!-- Bonus awarded (mobile) -->
                        <div v-if="order.is_payed && order.bonus_awarded > 0" class="mt-3 pt-3 border-t border-gray-200 text-sm text-green-700">
                            <span class="font-semibold">Нараховано бонусів:</span>
                            <span class="font-bold ml-1">+{{ Number(order.bonus_awarded).toFixed(2) }} грн</span>
                        </div>

                        <!-- Cancel order button (mobile) -->
                        <div v-if="hasCancellablePreOrder(order)" class="mt-3 pt-3 border-t border-gray-200">
                            <button @click="cancelOrder(order.id)"
                                    class="w-full flex justify-center items-center text-red-600 border border-red-300 bg-red-50 hover:bg-red-100 px-3 py-2 rounded text-sm font-semibold transition">
                                Скасувати замовлення
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <pagination class="mt-6" :links="orders.links"/>
        </div>
        <update-date-modal
            v-if="modalShow"
            v-model:show="modalShow"
            :pre-order="editingPreOrder"
        />
        <cabinet-attribute-modal
            v-if="attributeModalShow"
            v-model:show="attributeModalShow"
            :pre-order="attributePreOrder"
            :user="user"
        />
    </div>
</template>

<script>
import {Head} from '@inertiajs/inertia-vue3'
import Header from "@/Component/Header"
import {Link} from '@inertiajs/inertia-vue3'
import Checkbox from "@/Shared/Checkbox";
import Icon from "@/Shared/Icon.vue";
import Pagination from "@/Shared/Pagination";
import {route} from "ziggy-js";
import LoadingButton from "@/Shared/LoadingButton.vue";
import UpdateDateModal from "@/Component/UpdateDateModal.vue";
import CabinetAttributeModal from "@/Component/CabinetAttributeModal.vue";
import FlashMessage from "@/Component/FlashMessage.vue";

export default {
    components: {
        LoadingButton,
        Pagination,
        Icon,
        Checkbox,
        UpdateDateModal,
        CabinetAttributeModal,
        FlashMessage,
        Header,
        Head,
        Link
    },
    data() {
        return {
            modalShow: false,
            editingPreOrder: null,
            expandedOrders: [],
            attributeModalShow: false,
            attributePreOrder: null,
            topupAmount: null,
            topupLoading: false,
        }
    },
    props: {
        cartCount: {
            type: Number,
            default: 0
        },
        user: {
            type: Object,
            default: () => ({}),
        },
        orders: Object,
        bonusPercent: {
            type: Number,
            default: 0,
        },
    },
    methods: {
        route,
        toggleOrder(id) {
            if (this.expandedOrders.includes(id)) {
                this.expandedOrders = this.expandedOrders.filter(orderId => orderId !== id);
            } else {
                this.expandedOrders.push(id);
            }
        },
        checkout(order_id) {
            this.$inertia.post(this.$route('cabinet.checkout', order_id))
        },
        payWithBalance(order_id) {
            if (!confirm('Оплатити замовлення з внутрішнього балансу?')) {
                return;
            }
            this.$inertia.post(this.$route('cabinet.order.pay-balance', order_id))
        },
        startEditDate(preOrder) {
            this.editingPreOrder = preOrder;
            this.modalShow = true;
        },
        openAttributesModal(preOrder) {
            this.attributePreOrder = preOrder;
            this.attributeModalShow = true;
        },
        cancelOrder(orderId) {
            if (!confirm('Ви впевнені, що хочете скасувати замовлення? Кошти будуть повернуті на внутрішній баланс.')) {
                return;
            }
            this.$inertia.post(this.$route('cabinet.order.cancel', orderId));
        },
        hasCancellablePreOrder(order) {
            return order.is_payed && order.preOrders.some(p => !p.is_cancel && this.isFutureDate(p.start_date));
        },
        couldBePayed(preOrder) {
            if (preOrder.is_cancel) {
                return false
            }
            const created_time = new Date(preOrder.created_at);
            const now = new Date();
            const diffTime = Math.abs(now - created_time);
            const diffMinutes = Math.ceil(diffTime / (1000 * 60));
            return diffMinutes < 30
        },
        formattedDate(dateString) {
            const date = new Date(dateString);
            return date.toLocaleDateString('uk-UK', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        },
        isFutureDate(dateString) {
            const date = new Date(dateString);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return date > today;
        },
        encodeBase64(id) {
            return Buffer.from(id.toString()).toString('base64')
        },
        submitTopup() {
            if (!this.topupAmount || this.topupAmount < 1) return;
            this.topupLoading = true;
            this.$inertia.post(this.$route('cabinet.balance.topup'), { amount: this.topupAmount }, {
                onError: () => { this.topupLoading = false; },
            });
        },
    },
    computed: {
        isOwner() {
            return this.user && this.user.owner
        },
        flash() {
            return this.$page.props.flash || {}
        },
    }
}
</script>
